import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const AboutPage = () => {
  const timeline = [
    {
      year: '2006',
      title: 'Regional Victoria Acquisition',
      description: 'Founded Repose Hotels with the acquisition of a 60-room regional hotel. Successfully transformed the struggling property into a profitable operation within 14 months through strategic repositioning and operational excellence.'
    },
    {
      year: '2018',
      title: 'Melbourne Expansion',
      description: 'Acquired 78-apartment hotel in Maribyrnong, entering an untested market. Achieved 5-star TripAdvisor rating and #1 local ranking through disciplined management and corporate sales strategy.'
    },
    {
      year: '2020-2023',
      title: 'Award Recognition',
      description: 'Received 3× Franchise of the Year nominations, recognizing our commitment to operational excellence, guest satisfaction, and sustainable business growth.'
    },
    {
      year: 'Today',
      title: 'Strategic Partnerships',
      description: 'Expanding our portfolio through strategic partnerships and advisory services, helping hotel owners and investors maximize asset performance and achieve sustainable growth.'
    }
  ];

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We pursue operational excellence in every aspect of hotel management, from guest service to financial performance.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We build lasting relationships based on trust, transparency, and shared success with all stakeholders.'
    },
    {
      icon: Target,
      title: 'Discipline',
      description: 'Financial discipline and data-driven decision making guide our approach to asset management and operations.'
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'We focus on sustainable, long-term growth that delivers value to owners, guests, and team members alike.'
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1621293954908-907159247fc8)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-8 text-center pt-24">
          <h1 className="font-playfair text-5xl md:text-7xl font-bold mb-6 tracking-wide">
            About Repose Hotels
          </h1>
          <p className="text-xl md:text-2xl font-light max-w-3xl">
            Strategic Hotel Asset Partners with a proven track record of transforming hospitality investments
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-8">
                Who We Are
              </h2>
              <p className="text-xl text-[#3A2E24] leading-relaxed mb-6">
                Repose Hotels Pty Ltd is a privately owned hospitality investment and management company with a proven track record of transforming underperforming assets into high-performing hotel businesses.
              </p>
              <p className="text-xl text-[#3A2E24] leading-relaxed mb-6">
                Founded in 2006, we combine over 40 years of industry experience across hotel operations, franchise management, corporate travel, and strategic business development.
              </p>
              <p className="text-xl text-[#3A2E24] leading-relaxed font-semibold text-[#B89C4A]">
                We don't just advise — we operate.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/32978233/pexels-photo-32978233.jpeg" 
                alt="Repose Hotels"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            <div className="text-center p-6 bg-[#F4F4F4] rounded-lg">
              <div className="text-5xl font-bold text-[#B89C4A] mb-2">40+</div>
              <div className="text-[#3A2E24] font-medium">Years Combined Experience</div>
            </div>
            <div className="text-center p-6 bg-[#F4F4F4] rounded-lg">
              <div className="text-5xl font-bold text-[#B89C4A] mb-2">3×</div>
              <div className="text-[#3A2E24] font-medium">Franchise Award Nominations</div>
            </div>
            <div className="text-center p-6 bg-[#F4F4F4] rounded-lg">
              <div className="text-5xl font-bold text-[#B89C4A] mb-2">#1</div>
              <div className="text-[#3A2E24] font-medium">Market Rankings Achieved</div>
            </div>
            <div className="text-center p-6 bg-[#F4F4F4] rounded-lg">
              <div className="text-5xl font-bold text-[#B89C4A] mb-2">5★</div>
              <div className="text-[#3A2E24] font-medium">TripAdvisor Ratings</div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-16 text-center">
            Our Journey
          </h2>
          <div className="space-y-12">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-32">
                  <div className="text-4xl font-bold text-[#B89C4A] font-playfair">{item.year}</div>
                </div>
                <div className="flex-grow">
                  <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300">
                    <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">{item.title}</h3>
                    <p className="text-lg text-[#3A2E24] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6 text-center">
            Our Values
          </h2>
          <p className="text-xl text-[#3A2E24] text-center mb-16 max-w-3xl mx-auto">
            These core values guide everything we do, from strategic decisions to daily operations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-20 h-20 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">{value.title}</h3>
                  <p className="text-[#3A2E24] leading-relaxed">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-32 px-8 bg-[#2B2B2B] text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <h2 className="font-playfair text-4xl font-bold mb-6 text-[#B89C4A]">Our Vision</h2>
              <p className="text-xl text-gray-200 leading-relaxed">
                To set new standards in hospitality asset performance by blending disciplined financial strategy with genuine guest-focused service culture.
              </p>
            </div>
            <div>
              <h2 className="font-playfair text-4xl font-bold mb-6 text-[#B89C4A]">Our Commitment</h2>
              <ul className="space-y-4 text-lg text-gray-200">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                  <span>Transparent reporting and accountability</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                  <span>Hands-on operational leadership</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                  <span>Long-term value creation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                  <span>Exceptional guest experiences</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                  <span>Strategic, sustainable growth</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-8">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl text-[#3A2E24] leading-relaxed mb-12">
            Let's discuss how our expertise can help transform your hospitality investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              size="lg" 
              className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/contact">Contact Us <ArrowRight className="ml-2" /></Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-[#2B2B2B] text-[#2B2B2B] hover:bg-[#2B2B2B] hover:text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/services">Our Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
